/**
 * 入力された全群をまとめて分析する層。
 * UI はこのモジュールだけを呼べばよい。
 */

import {
  alphaFromConfidence,
  compareProportions,
  estimateRate,
  type Comparison,
} from './proportion'
import { minimumDetectableEffect, type MdeResult } from './power'
import {
  compareMeans,
  estimateMean,
  isUsableSample,
  meanMde,
  type MeanComparison,
  type MeanEstimate,
} from './continuous'
import {
  adjustPValues,
  intervalAlpha,
  type CorrectionMethod,
} from './multiple'
import {
  detectSampleRatioMismatch,
  SRM_ALERT_THRESHOLD,
  type SrmResult,
} from './srm'
import {
  computeBoundaries,
  equallySpacedTimes,
  SPENDING_LABELS,
  type Boundary,
  type SpendingFunction,
} from './sequential'
import type { RateEstimate, Tail } from './types'
import { formatAlpha } from '../format'

export interface GroupInput {
  id: string
  name: string
  visitors: number
  conversions: number
  /**
   * 設計上の割付比。省略時は 1（均等割付）。
   * 80:20 のような不均等割付を均等前提で SRM 検定すると、
   * 正常な実験を「振り分けが壊れている」と誤って警告してしまう。
   */
  weight?: number
}

/** 途中経過を見ながら進める実験の設定。 */
export interface SequentialSettings {
  /** 実験全体で予定している確認の回数 */
  looks: number
  /** 今回が何回目の確認か (1 以上 looks 以下) */
  currentLook: number
  spending: SpendingFunction
}

export interface AnalysisSettings {
  confidence: number
  tail: Tail
  correction: CorrectionMethod
  /** 「有意差なし」の解釈に使う目標検出力 */
  diagnosticPower: number
  /** 途中で結果を見る場合の設定。null なら最後に 1 回だけ見る通常の検定。 */
  sequential: SequentialSettings | null
}

export type IssueLevel = 'error' | 'warning' | 'info'

export interface Issue {
  level: IssueLevel
  /** 該当する群の id。全体に関わる指摘なら undefined。 */
  groupId?: string
  title: string
  detail: string
  /**
   * この指摘があるかぎり判定を結論として扱えない、という印。
   *
   * 統計以前の理由で数字が壊れている場合に立てる。level が error でも、
   * 直せば済む入力の不備（設定の書き間違いなど）には立てない。
   */
  blocking?: boolean
}

/**
 * 判定を結論として表に出してよいかどうか。
 *
 * 警告を判定の上に並べるだけでは足りない。警告は読み飛ばされ、
 * 色のついた判定と効果量だけが持ち出される。数字が壊れているときは
 * 判定そのものを既定で伏せ、見るかどうかを読む側の操作にする。
 */
export function hasBlockingIssue(issues: readonly Issue[]): boolean {
  return issues.some((issue) => issue.blocking === true)
}

/** 判定を伏せている理由。画面とレポートで同じ文言を出すために使う。 */
export function blockingReasons(issues: readonly Issue[]): string[] {
  return issues.filter((issue) => issue.blocking === true).map((issue) => issue.title)
}

export interface VariantResult {
  group: GroupInput
  comparison: Comparison
  rawPValue: number | null
  adjustedPValue: number | null
  /** 補正後 p 値が有意水準を下回ったか */
  significant: boolean
  /**
   * この人数で目標検出力を満たすのに必要だった効果量。
   * 「有意差なし」がサンプル不足なのか本当に差がないのかを切り分ける。
   */
  mde: MdeResult | null
}

/** 逐次確認を有効にしたときの、この回の位置づけ。 */
export interface SequentialContext {
  settings: SequentialSettings
  /** 予定している全確認時点の棄却限界 */
  boundaries: Boundary[]
  /** 今回の確認に対応する棄却限界 */
  current: Boundary
}

export interface AnalysisResult {
  control: { group: GroupInput; estimate: RateEstimate }
  variants: VariantResult[]
  comparisonCount: number
  /**
   * 判定に使った有意水準。逐次確認を有効にすると、
   * この回に配分された分だけ名目の水準より厳しくなる。
   */
  effectiveAlpha: number
  /** 信頼区間に使った有意水準（多重比較補正まで含む） */
  intervalAlpha: number
  srm: SrmResult | null
  sequential: SequentialContext | null
  issues: Issue[]
  /**
   * 判定を結論として扱えない状態か。issues から導出しているので、
   * 画面もレポートもこの 1 つを見れば同じ扱いになる。
   */
  resultsBlocked: boolean
}

export interface ValidationError {
  groupId: string
  field: 'visitors' | 'conversions' | 'weight'
  message: string
}

/** 入力値の妥当性検査。分析の前に UI がこれを呼ぶ。 */
export function validateGroups(groups: readonly GroupInput[]): ValidationError[] {
  const errors: ValidationError[] = []
  for (const group of groups) {
    const { visitors, conversions } = group
    if (!Number.isFinite(visitors) || !Number.isInteger(visitors) || visitors < 1) {
      errors.push({
        groupId: group.id,
        field: 'visitors',
        message: '1 以上の整数を入力してください',
      })
    }
    if (
      !Number.isFinite(conversions) ||
      !Number.isInteger(conversions) ||
      conversions < 0
    ) {
      errors.push({
        groupId: group.id,
        field: 'conversions',
        message: '0 以上の整数を入力してください',
      })
    }
    if (
      Number.isFinite(visitors) &&
      Number.isFinite(conversions) &&
      conversions > visitors
    ) {
      errors.push({
        groupId: group.id,
        field: 'conversions',
        message: '成果数が訪問者数を超えています',
      })
    }
    if (group.weight !== undefined) {
      if (!Number.isFinite(group.weight) || group.weight <= 0) {
        errors.push({
          groupId: group.id,
          field: 'weight',
          message: '0 より大きい数を入力してください',
        })
      }
    }
  }
  return errors
}

/** 正規近似が信頼できる最小の期待度数。これを下回ると z 検定の前提が崩れる。 */
const MIN_EXPECTED_COUNT = 5

export function analyze(
  groups: readonly GroupInput[],
  settings: AnalysisSettings,
): AnalysisResult | null {
  if (groups.length < 2) return null
  const [controlGroup, ...variantGroups] = groups

  const alpha = alphaFromConfidence(settings.confidence)
  const comparisonCount = variantGroups.length
  const sequential = buildSequentialContext(alpha, settings)
  // 途中で覗いた回数のぶんだけ、この回に使ってよい水準は狭くなる。
  // 判定も信頼区間もこの水準で行い、両者が同じ基準を指すようにする。
  const effectiveAlpha = sequential
    ? sequential.current.nominalAlpha
    : alpha
  const ciAlpha = intervalAlpha(
    effectiveAlpha,
    comparisonCount,
    settings.correction,
  )

  const control = {
    n: controlGroup.visitors,
    x: controlGroup.conversions,
  }

  const comparisons = variantGroups.map((group) =>
    compareProportions(
      control,
      { n: group.visitors, x: group.conversions },
      {
        confidence: settings.confidence,
        tail: settings.tail,
        adjustedAlpha: ciAlpha,
      },
    ),
  )

  const rawPValues = comparisons.map((c) => c.pValue)
  const adjusted = adjustPValues(rawPValues, settings.correction)

  const variants: VariantResult[] = variantGroups.map((group, i) => {
    const comparison = comparisons[i]
    const adjustedPValue = adjusted[i]
    return {
      group,
      comparison,
      rawPValue: rawPValues[i],
      adjustedPValue,
      significant: adjustedPValue !== null && adjustedPValue < effectiveAlpha,
      mde: minimumDetectableEffect({
        baselineRate: comparison.control.rate,
        n1: control.n,
        n2: group.visitors,
        alpha: effectiveAlpha,
        power: settings.diagnosticPower,
        tail: settings.tail,
        // 下がった群に増加方向の MDE を出すと基準がずれる。
        // 比率が 0 / 1 に寄ると必要な効果量は方向で非対称になるため、
        // 観測された向きに合わせて計算する。
        direction: comparison.absoluteDiff < 0 ? 'decrease' : 'increase',
      }),
    }
  })

  const srm = detectSampleRatioMismatch(
    groups.map((g) => g.visitors),
    groups.map((g) => g.weight ?? 1),
  )

  const issues = collectIssues(
    groups,
    settings,
    srm,
    comparisonCount,
    sequential,
    variants,
    alpha,
  )

  return {
    control: {
      group: controlGroup,
      estimate: estimateRate(control, 1 - ciAlpha),
    },
    variants,
    comparisonCount,
    effectiveAlpha,
    intervalAlpha: ciAlpha,
    srm,
    sequential,
    issues,
    resultsBlocked: hasBlockingIssue(issues),
  }
}

/**
 * 逐次確認の設定から、この回の棄却限界を求める。
 * 設定が壊れている場合は null を返し、通常の検定として扱う
 * （その旨は collectIssues がエラーとして報告する）。
 */
function buildSequentialContext(
  alpha: number,
  settings: AnalysisSettings,
): SequentialContext | null {
  const sequential = settings.sequential
  if (!sequential) return null
  if (!isValidSequential(sequential)) return null

  const boundaries = computeBoundaries({
    alpha,
    informationTimes: equallySpacedTimes(sequential.looks),
    tail: settings.tail,
    spending: sequential.spending,
  })
  if (!boundaries) return null

  return {
    settings: sequential,
    boundaries,
    current: boundaries[sequential.currentLook - 1],
  }
}

/** 確認回数は整数で、今回が何回目かはその範囲に収まっていなければならない。 */
export function isValidSequential(sequential: SequentialSettings): boolean {
  const { looks, currentLook } = sequential
  return (
    Number.isInteger(looks) &&
    looks >= 1 &&
    Number.isInteger(currentLook) &&
    currentLook >= 1 &&
    currentLook <= looks
  )
}

function collectIssues(
  groups: readonly GroupInput[],
  settings: AnalysisSettings,
  srm: SrmResult | null,
  comparisonCount: number,
  sequential: SequentialContext | null,
  variants: readonly VariantResult[],
  plainAlpha: number,
): Issue[] {
  const issues: Issue[] = []

  for (const group of groups) {
    const successes = group.conversions
    const failures = group.visitors - group.conversions

    if (successes === 0) {
      issues.push({
        level: 'warning',
        groupId: group.id,
        title: `${group.name}: 成果数が 0 です`,
        detail:
          '比率が 0 の群は相対リフトを計算できません。絶対差と信頼区間で判断してください。',
      })
    } else if (
      Math.min(successes, failures) < MIN_EXPECTED_COUNT
    ) {
      issues.push({
        level: 'warning',
        groupId: group.id,
        title: `${group.name}: サンプルが少なすぎます`,
        detail:
          `成果数 ${successes} 件 / 非成果数 ${failures} 件。どちらも ${MIN_EXPECTED_COUNT} 件以上ないと` +
          ' z 検定の正規近似が成り立たず、p 値も信頼区間も実際よりも狭く出ます。',
      })
    }
  }

  if (srm && srm.pValue < SRM_ALERT_THRESHOLD) {
    const observed = formatShares(srm.observedShares)
    const expected = formatShares(srm.expectedShares)
    const assumedEqual = groups.every((g) => (g.weight ?? 1) === (groups[0].weight ?? 1))
    issues.push({
      level: 'error',
      // 振り分けが壊れている実験の判定は、直しようがない。
      // 設定を書き間違えた場合と違い、この数字自体を採否の根拠にできない。
      blocking: true,
      title: 'サンプル比率が偏っています (SRM)',
      detail:
        `実際の人数比は ${observed} で、設計した割付比 ${expected} からの乖離は` +
        `偶然では説明できません (p = ${srm.pValue.toExponential(1)})。` +
        '振り分け・計測・重複除外のいずれかが壊れている可能性が高く、' +
        'この状態の検定結果は統計以前の理由で信用できません。' +
        // 均等割付を前提に判定しているときだけ、不均等割付の設計を疑う道を示す。
        // 80:20 で振った正常な実験がここに落ちるのが最も多い誤検出のため。
        (assumedEqual
          ? 'そもそも均等割付でない設計なら、観測データの「割付比を指定」から' +
            '設計どおりの比率を入れ直してください。'
          : ''),
    })
  }

  if (comparisonCount > 1 && settings.correction === 'none') {
    const alpha = alphaFromConfidence(settings.confidence)
    const familyWiseRate = 1 - (1 - alpha) ** comparisonCount
    issues.push({
      level: 'warning',
      title: `${comparisonCount} 件の比較を補正なしで検定しています`,
      detail:
        'すべての群に本当は差がなくても、どれかが有意になる確率は約 ' +
        `${(familyWiseRate * 100).toFixed(0)}% あります。Holm 補正の使用を検討してください。`,
    })
  }

  issues.push(...sequentialIssues(settings, sequential, variants, plainAlpha))

  return issues
}

/**
 * 逐次確認に関する指摘。比率と平均で同じ文言を出すため切り出している。
 *
 * @param variants 判定済みの比較群。通常なら有意になる差が
 *   この回の基準に届かなかった場合を拾うために使う。
 */
function sequentialIssues(
  settings: AnalysisSettings,
  sequential: SequentialContext | null,
  variants: readonly { group: { name: string }; adjustedPValue: number | null; significant: boolean }[],
  plainAlpha: number,
): Issue[] {
  const issues: Issue[] = []

  if (settings.sequential && !sequential) {
    const { looks, currentLook } = settings.sequential
    issues.push({
      level: 'error',
      title: '逐次確認の設定が不正です',
      detail:
        `「今回が何回目か」(${describeCount(currentLook)}) は 1 以上 ` +
        `${describeCount(looks)} 以下の整数にしてください。` +
        '予定回数を減らしたときは、今回が何回目かも合わせて直す必要があります。' +
        '直すまでは、途中で覗いた回数を考慮しない通常の検定として判定しているため、' +
        `基準は ${formatAlpha(plainAlpha)} のままです。`,
    })
  }

  if (sequential) {
    const { current, settings: sq } = sequential
    // 補正後 p 値を判定に使っているときは、どちらの補正の話かを取り違えないよう
    // 「補正なし」とは書かず、何と比べているのかを名指しする
    const correctionApplied = settings.correction !== 'none' && variants.length > 1
    const pLabel = correctionApplied ? '多重比較補正後の p 値' : 'p 値'

    issues.push({
      level: 'info',
      title: `${sq.currentLook} 回目 / 全 ${sq.looks} 回の確認として判定しています`,
      detail:
        `予定サンプルサイズに対する進捗は ${(current.informationTime * 100).toFixed(0)}% です。` +
        `${SPENDING_LABELS[sq.spending]} 型のアルファ消費関数でこの回に配分される水準は ` +
        `${formatAlpha(current.nominalAlpha)}（棄却限界 z = ${current.z.toFixed(3)}）で、` +
        `途中確認を宣言しない場合の ${formatAlpha(plainAlpha)} より厳しくなります。` +
        '何回覗いても全体の偽陽性率が有意水準を超えないよう、' +
        `ここまでに累計 ${formatAlpha(current.cumulativeAlpha)} を配分しています。` +
        (correctionApplied
          ? '（多重比較補正と重ねる場合、全体の偽陽性率は名目の有意水準を' +
            'わずかに上回ることがあります。実測で比較 3 件のとき 5.4% 程度です。）'
          : ''),
    })

    // 通常の水準なら有意になる差は、覗いた回数を数えると足りていない。
    // 判定が覆るのはこの機能の核心なので、黙って落とさず理由を出す。
    const heldBack = variants.filter(
      (v) =>
        v.adjustedPValue !== null &&
        !v.significant &&
        v.adjustedPValue < plainAlpha,
    )
    if (heldBack.length > 0) {
      issues.push({
        level: 'warning',
        title: `途中で止めるには証拠が足りません（${heldBack
          .map((v) => v.group.name)
          .join('、')}）`,
        detail:
          `${pLabel}は、途中確認を宣言しない場合の基準 ${formatAlpha(plainAlpha)} を` +
          `下回っていますが、${sq.currentLook} 回目の確認に配分された ` +
          `${formatAlpha(current.nominalAlpha)} には届いていません。` +
          'この状態で「有意になったから」と実験を止めると、' +
          '途中で覗いた回数のぶんだけ偽陽性を拾います（宣言せずに 5 回覗くと、' +
          '本当は差がなくてもどれかの時点で有意になる確率は約 14% です）。',
      })
    }
  }

  return issues
}

/** 設定値をそのまま文に出す。空欄や小数も「何が入っているか」が分かる形にする。 */
function describeCount(value: number): string {
  return Number.isFinite(value) ? String(value) : '未入力'
}

/** 比較群 1 つに対する結論。 */
export type Verdict = 'improved' | 'worsened' | 'inconclusive'

/**
 * 判定の結論を返す。
 * 画面とレポートで同じ結論を出すために、この関数を唯一の判断箇所にする。
 * 比率と平均のどちらの結果でも使えるよう、必要な形だけを引数に取る。
 */
export function verdictOf(variant: {
  significant: boolean
  comparison: { absoluteDiff: number }
}): Verdict {
  if (!variant.significant) return 'inconclusive'
  return variant.comparison.absoluteDiff > 0 ? 'improved' : 'worsened'
}

/** 構成比を「50.0% : 50.0%」の形にする。 */
function formatShares(shares: readonly number[]): string {
  return shares.map((s) => `${(s * 100).toFixed(1)}%`).join(' : ')
}

/* ============================================================
   連続値の指標（平均の比較）
   ============================================================ */

/**
 * 1 群の要約統計量。
 *
 * 生データではなく人数・平均・標準偏差の 3 つだけを入力にする。
 * 個票を扱わないことで、このツールの「数値がブラウザの外に出ない」前提を
 * 変えずに済み、集計済みの数字しか手元にない状況にも合う。
 */
export interface MeanGroupInput {
  id: string
  name: string
  /** 人数（この指標を観測した対象の数） */
  n: number
  mean: number
  /** 標本標準偏差（不偏、分母 n − 1） */
  sd: number
}

export interface MeanValidationError {
  groupId: string
  field: 'n' | 'mean' | 'sd'
  message: string
}

export interface MeanVariantResult {
  group: MeanGroupInput
  comparison: MeanComparison
  rawPValue: number | null
  adjustedPValue: number | null
  significant: boolean
  /** この人数で目標検出力を確保できる最小の差 */
  mde: number | null
}

export interface MeanAnalysisResult {
  control: { group: MeanGroupInput; estimate: MeanEstimate }
  variants: MeanVariantResult[]
  comparisonCount: number
  effectiveAlpha: number
  intervalAlpha: number
  sequential: SequentialContext | null
  issues: Issue[]
  /** 比率の分析と同じ意味。@see AnalysisResult.resultsBlocked */
  resultsBlocked: boolean
}

/** 平均の比較で最低限必要な人数。これを下回ると分散が定義できない。 */
const MIN_SAMPLE_SIZE = 2

/** 正規近似が怪しくなる人数。中心極限定理に頼るには心もとない。 */
const SMALL_SAMPLE_SIZE = 30

/** 分布の歪みを疑う変動係数（標準偏差 / 平均）。 */
const SKEW_ALERT_CV = 2

export function validateMeanGroups(
  groups: readonly MeanGroupInput[],
): MeanValidationError[] {
  const errors: MeanValidationError[] = []
  for (const group of groups) {
    if (
      !Number.isFinite(group.n) ||
      !Number.isInteger(group.n) ||
      group.n < MIN_SAMPLE_SIZE
    ) {
      errors.push({
        groupId: group.id,
        field: 'n',
        message: `${MIN_SAMPLE_SIZE} 以上の整数を入力してください`,
      })
    }
    if (!Number.isFinite(group.mean)) {
      errors.push({
        groupId: group.id,
        field: 'mean',
        message: '数値を入力してください',
      })
    }
    if (!Number.isFinite(group.sd) || group.sd < 0) {
      errors.push({
        groupId: group.id,
        field: 'sd',
        message: '0 以上の数値を入力してください',
      })
    }
  }
  return errors
}

/** 平均の指標について全群をまとめて分析する。 */
export function analyzeMeans(
  groups: readonly MeanGroupInput[],
  settings: AnalysisSettings,
): MeanAnalysisResult | null {
  if (groups.length < 2) return null
  const [controlGroup, ...variantGroups] = groups

  const alpha = alphaFromConfidence(settings.confidence)
  const comparisonCount = variantGroups.length
  const sequential = buildSequentialContext(alpha, settings)
  const effectiveAlpha = sequential ? sequential.current.nominalAlpha : alpha
  const ciAlpha = intervalAlpha(
    effectiveAlpha,
    comparisonCount,
    settings.correction,
  )

  const control = { n: controlGroup.n, mean: controlGroup.mean, sd: controlGroup.sd }

  const comparisons = variantGroups.map((group) =>
    compareMeans(
      control,
      { n: group.n, mean: group.mean, sd: group.sd },
      {
        confidence: settings.confidence,
        tail: settings.tail,
        adjustedAlpha: ciAlpha,
      },
    ),
  )

  const rawPValues = comparisons.map((c) => c.pValue)
  const adjusted = adjustPValues(rawPValues, settings.correction)

  const variants: MeanVariantResult[] = variantGroups.map((group, i) => {
    const comparison = comparisons[i]
    const adjustedPValue = adjusted[i]
    return {
      group,
      comparison,
      rawPValue: rawPValues[i],
      adjustedPValue,
      significant: adjustedPValue !== null && adjustedPValue < effectiveAlpha,
      mde: meanMde({
        standardError: comparison.standardError,
        alpha: effectiveAlpha,
        power: settings.diagnosticPower,
        tail: settings.tail,
      }),
    }
  })

  const issues = collectMeanIssues(
    groups,
    settings,
    comparisonCount,
    sequential,
    variants,
    alpha,
  )

  return {
    control: {
      group: controlGroup,
      estimate: estimateMean(control, 1 - ciAlpha),
    },
    variants,
    comparisonCount,
    effectiveAlpha,
    intervalAlpha: ciAlpha,
    sequential,
    issues,
    resultsBlocked: hasBlockingIssue(issues),
  }
}

function collectMeanIssues(
  groups: readonly MeanGroupInput[],
  settings: AnalysisSettings,
  comparisonCount: number,
  sequential: SequentialContext | null,
  variants: readonly MeanVariantResult[],
  plainAlpha: number,
): Issue[] {
  const issues: Issue[] = []

  for (const group of groups) {
    if (!isUsableSample({ n: group.n, mean: group.mean, sd: group.sd })) continue

    if (group.sd === 0) {
      issues.push({
        level: 'warning',
        groupId: group.id,
        title: `${group.name}: ばらつきが 0 です`,
        detail:
          '全員が同じ値だったことになります。集計の単位が違う（合計値を入れている、' +
          '対象を絞りすぎている）可能性があるので、標準偏差の出し方を確認してください。',
      })
    } else if (group.mean > 0 && group.sd / group.mean > SKEW_ALERT_CV) {
      issues.push({
        level: 'warning',
        groupId: group.id,
        title: `${group.name}: ばらつきが平均の ${(group.sd / group.mean).toFixed(1)} 倍あります`,
        detail:
          '売上のようにゼロが多く裾の長い指標では、平均の差は少数の外れ値に引きずられます。' +
          '高額購入が 1 件増えただけで結論が変わることがあるため、' +
          '購入者に絞った平均や、外れ値を丸めた指標でも確かめてください。',
      })
    }

    if (group.n < SMALL_SAMPLE_SIZE) {
      issues.push({
        level: 'warning',
        groupId: group.id,
        title: `${group.name}: サンプルが少なすぎます (${group.n} 件)`,
        detail:
          `${SMALL_SAMPLE_SIZE} 件を下回ると、平均の分布が正規分布に近いという前提が` +
          '確かめにくくなります。t 検定は元の分布の歪みには強くないため、' +
          'この人数での p 値と信頼区間は目安として扱ってください。',
      })
    }
  }

  if (comparisonCount > 1 && settings.correction === 'none') {
    const alpha = alphaFromConfidence(settings.confidence)
    const familyWiseRate = 1 - (1 - alpha) ** comparisonCount
    issues.push({
      level: 'warning',
      title: `${comparisonCount} 件の比較を補正なしで検定しています`,
      detail:
        'すべての群に本当は差がなくても、どれかが有意になる確率は約 ' +
        `${(familyWiseRate * 100).toFixed(0)}% あります。Holm 補正の使用を検討してください。`,
    })
  }

  issues.push(...sequentialIssues(settings, sequential, variants, plainAlpha))
  return issues
}
